import * as d3 from 'd3';
import {
  visibleDatasets, domainFor, resolveDomain, clampRange, toCsv,
} from '@/util/chartState';
import {
  subscribe, publish, HOVER, ZOOM,
} from '@/util/chartSync';
import { formatDateTime } from '@/util/date';

function unduh(blob, nama) {
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nama;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// State toolkit hidup lebih lama dari komponennya — chart dibongkar-pasang tiap filter di-Apply.
const simpanan = {};

// Cangkang tipis: state interaksi chart, keputusan diteruskan ke chartState/chartSync.
export default {
  data() {
    return {
      hiddenSeries: {},
      zoomRange: null,
      lockedY: null,
      tkLepas: [],
      tkLepasHover: null,
    };
  },
  methods: {
    tkVisible(datasets) {
      return visibleDatasets(datasets, this.hiddenSeries);
    },
    tkDomain(axis, datasets, fallbackMax, extra) {
      const dihitung = domainFor(datasets, axis, fallbackMax);
      const tambahan = (extra || []).filter((v) => typeof v === 'number' && Number.isFinite(v));
      // Digabung sebelum kunci diterapkan, biar sumbu terkunci ga bisa ditembus puncak.
      const gabung = tambahan.length
        ? [Math.min(dihitung[0], ...tambahan), Math.max(dihitung[1], ...tambahan)]
        : dihitung;
      return resolveDomain(this.lockedY ? this.lockedY[axis] : null, gabung);
    },
    tkXDomain(timestamps) {
      const penuh = d3.extent(timestamps);
      if (!this.zoomRange) return penuh;
      const dijepit = clampRange(this.zoomRange, [+penuh[0], +penuh[1]]);
      if (!dijepit) {
        // Rentang zoom lama ga nyentuh data baru, jadi dibuang.
        this.zoomRange = null;
        return penuh;
      }
      return [new Date(dijepit[0]), new Date(dijepit[1])];
    },
    tkToggleSeries(nama) {
      this.$set(this.hiddenSeries, nama, !this.hiddenSeries[nama]);
      this.tkSimpan();
      this.drawChart();
    },
    tkToggleLockY(domains) {
      this.lockedY = this.lockedY ? null : domains;
      this.tkSimpan();
      this.drawChart();
    },
    tkInstallBrush(svg, x, width, height) {
      const brush = d3.brushX().extent([[0, 0], [width, height]])
        .on('end', (event) => {
          // Brush.move(null) di bawah numbuk event end lagi, jadi tanpa cek ini rekursi ga abis-abis.
          if (!event.selection) return;
          const [a, b] = event.selection.map((px) => +x.invert(px));
          svg.select('.tk-brush').call(brush.move, null);
          publish(ZOOM, [a, b]);
        });
      const g = svg.append('g').attr('class', 'tk-brush').call(brush)
        .on('dblclick', () => publish(ZOOM, null));
      // Hover nempel di overlay brush; rect sendiri bakal ketutup dan tooltipnya mati.
      return g.select('.overlay');
    },
    tkPublishHover(ms) {
      publish(HOVER, ms);
    },
    tkPublishZoom(rentang) {
      publish(ZOOM, rentang);
    },
    // Dipanggil tiap gambar ulang, jadi yang lama wajib dilepas dulu.
    tkOnHover(fn) {
      if (this.tkLepasHover) this.tkLepasHover();
      this.tkLepasHover = subscribe(HOVER, fn);
    },
    tkKunci() {
      const dasar = this.$options.name || 'chart';
      const rute = this.$route ? (this.$route.name || this.$route.path || '') : '';
      const kapal = this.$store ? this.$store.getters['filter/currentMassId'] : '';
      const bagian = [dasar, rute || '-', kapal || '-'];
      if (this.type) bagian.push(this.type);
      return bagian.join(':');
    },
    tkSimpan() {
      simpanan[this.tkKunci()] = {
        hiddenSeries: this.hiddenSeries,
        lockedY: this.lockedY,
      };
    },
    tkBind() {
      const tersimpan = simpanan[this.tkKunci()];
      if (tersimpan) {
        this.hiddenSeries = { ...tersimpan.hiddenSeries };
        this.lockedY = tersimpan.lockedY;
      }
      // Handler ZOOM cuma set state + gambar ulang, gak publish ZOOM lagi.
      this.tkLepas.push(subscribe(ZOOM, (rentang) => {
        if (this._isDestroyed) return;
        this.zoomRange = rentang;
        this.tkSimpan();
        this.drawChart();
      }));
    },
    tkUnbind() {
      if (this.tkLepasHover) this.tkLepasHover();
      this.tkLepasHover = null;
      this.tkLepas.forEach((lepas) => lepas());
      this.tkLepas = [];
    },
    // Nama berkas ikut rentang yang keliatan dan kerincian yang dipakai.
    tkNamaBerkas(dasar) {
      let rentang = this.zoomRange;
      let kerincian = '';
      try {
        const g = this.$store ? this.$store.getters : null;
        if (g) {
          const tanggal = g['filter/date'];
          if (!rentang && tanggal && tanggal.start && tanggal.end) rentang = [tanggal.start, tanggal.end];
          kerincian = String(g['filter/interval'] || '').toLowerCase();
        }
      } catch (e) {
        rentang = this.zoomRange;
      }
      if (!rentang) return dasar;
      const cap = (v) => formatDateTime(v, 'YYYYMMDD-HHmm', null, false);
      const bagian = [dasar, cap(rentang[0]), cap(rentang[1])];
      if (kerincian) bagian.push(kerincian);
      return bagian.join('_');
    },
    tkExportCsv(namaBerkas, timestamps, datasets) {
      const isi = toCsv({
        timestamps: timestamps.map((t) => +t),
        datasets,
        formatTs: (ms) => formatDateTime(ms, 'YYYY-MM-DD HH:mm:ss'),
      });
      unduh(new Blob([isi], { type: 'text/csv;charset=utf-8' }), `${this.tkNamaBerkas(namaBerkas)}.csv`);
    },
    tkExportPng(namaBerkas) {
      const svg = this.$refs.chartContainer.querySelector('svg');
      if (!svg) return;
      const kotak = svg.getBoundingClientRect();
      const teks = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = kotak.width * 2;
        canvas.height = kotak.height * 2;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => unduh(blob, `${this.tkNamaBerkas(namaBerkas)}.png`));
      };
      img.onerror = () => console.warn('Gagal export PNG chart');
      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(teks)}`;
    },
  },
};
